import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Inbox, UserCheck, AlertTriangle, Send, CheckCircle2, Lock, RotateCcw, UserPlus, ArrowLeft } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import { useNotifications } from "../context/NotificationContext";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const iconByType = {
  case_submitted: Inbox,
  case_claimed: UserCheck,
  needs_correction: AlertTriangle,
  case_resubmitted: Send,
  case_approved: CheckCircle2,
  case_closed: Lock,
  case_force_released: RotateCcw,
  case_reassigned: UserPlus,
};

const colorByType = {
  case_submitted: "text-blue-600 bg-blue-50",
  case_claimed: "text-indigo-600 bg-indigo-50",
  needs_correction: "text-rose-600 bg-rose-50",
  case_resubmitted: "text-violet-600 bg-violet-50",
  case_approved: "text-emerald-600 bg-emerald-50",
  case_closed: "text-slate-600 bg-slate-100",
  case_force_released: "text-amber-700 bg-amber-50",
  case_reassigned: "text-indigo-600 bg-indigo-50",
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

const groupByDate = (items) => {
  const today = startOfDay(new Date());
  const yesterday = today - 86400 * 1000;
  const out = { today: [], yesterday: [], earlier: [] };
  for (const n of items) {
    const t = startOfDay(n.created_at);
    if (t === today) out.today.push(n);
    else if (t === yesterday) out.yesterday.push(n);
    else out.earlier.push(n);
  }
  return out;
};

const formatTime = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { markRead, markAllRead, refreshCount, unreadCount } = useNotifications();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", {
        params: { limit: 100, unread_only: filter === "unread" },
      });
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const onRowClick = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.case_id) navigate(`/cases/${n.case_id}`);
    else if (n.type === "password_reset") navigate("/profile?tab=security");
  };

  const grouped = groupByDate(items);

  const renderGroup = (label, list) => {
    if (list.length === 0) return null;
    return (
      <div className="space-y-2" data-testid={`notif-group-${label}`}>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold px-2">{label}</p>
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
          {list.map((n) => {
            const Icon = iconByType[n.type] || Bell;
            const color = colorByType[n.type] || "text-slate-600 bg-slate-100";
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => onRowClick(n)}
                className={`w-full text-left px-5 py-4 flex gap-4 hover:bg-slate-50 focus-ring transition-colors ${
                  n.read ? "" : "bg-blue-50/30"
                }`}
                data-testid={`notif-row-${n.id}`}
              >
                <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-snug ${n.read ? "text-slate-600" : "text-[#0A2540] font-medium"}`}>
                    {n.message}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {n.case_applicant_name ? `Case · ${n.case_applicant_name} · ` : ""}
                    {formatTime(n.created_at)}
                  </p>
                </div>
                {!n.read ? (
                  <span className="mt-2 h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="notifications-page">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-sm text-slate-500 hover:text-[#0A2540] mb-2 focus-ring rounded px-1"
              data-testid="notifications-back"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t("nav_back")}
            </button>
            <h2 className="font-display text-3xl tracking-tight font-medium text-[#0A2540]">
              {t("nav_notifications")}
            </h2>
          </div>
          {unreadCount > 0 ? (
            <Button
              variant="outline"
              onClick={async () => { await markAllRead(); load(); }}
              data-testid="notifications-mark-all"
            >
              {t("notif_mark_all_read")}
            </Button>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {[
            { v: "all", l: t("notif_filter_all") },
            { v: "unread", l: t("notif_filter_unread") },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setFilter(f.v)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors focus-ring ${
                filter === f.v
                  ? "bg-[#0A2540] text-white"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`notifications-filter-${f.v}`}
            >
              {f.l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-6 w-6 border-2 border-slate-300 border-t-[#0A2540] rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-16 text-center text-slate-400" data-testid="notifications-empty">
            <Bell className="h-8 w-8 mx-auto text-slate-300 mb-3" />
            <p className="text-sm">{t("notif_caught_up")}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {renderGroup(t("notif_today"), grouped.today)}
            {renderGroup(t("notif_yesterday"), grouped.yesterday)}
            {renderGroup(t("notif_earlier"), grouped.earlier)}
          </div>
        )}
      </main>
    </div>
  );
};

export default NotificationsPage;
